import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createApiClient } from '../lib/apiClient';
import { resolveInvite, signup, type Invite } from '../lib/auth/authApi';
import './auth.css';

const bare = createApiClient({ tokens: { get: () => null, set: () => {} } });

export default function WelcomeSignupScreen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('t') ?? '';

  const [invite, setInvite] = useState<Invite | null>(null);
  const [fullName, setFullName] = useState('');
  const [dob, setDob] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (token === '') { setError('This link is missing its invitation token.'); return; }
    resolveInvite(bare, token)
      .then((i) => { setInvite(i); if (i.clientDisplayName) setFullName(i.clientDisplayName); })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'This invitation could not be found.'));
  }, [token]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (invite === null) return;
    setBusy(true); setError('');
    try {
      const normalizedPhone = phone.replace(/[\s()\-]/g, '');
      await signup(bare, { token, fullName, dateOfBirth: dob, email: invite.clientEmail, phone: normalizedPhone });
      navigate(`/verify?email=${encodeURIComponent(invite.clientEmail)}&phone=${encodeURIComponent(normalizedPhone)}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-up failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="auth">
      <div className="auth__card">
        <p className="auth__kicker">Legacy Architect</p>
        <h1 className="auth__title">{invite?.clientDisplayName ? `Welcome, ${invite.clientDisplayName}.` : 'Begin your legacy.'}</h1>
        <p className="auth__sub">Your advisor has reserved a place for your voice. A few details to open your vault.</p>
        <p className="auth__alert" role="alert">{error}</p>
        <form onSubmit={onSubmit}>
          <label className="auth__field">
            <span className="auth__label">Email</span>
            <input className="auth__input" type="email" value={invite?.clientEmail ?? ''} readOnly aria-readonly="true" />
          </label>
          <label className="auth__field">
            <span className="auth__label">Full name</span>
            <input className="auth__input" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
          </label>
          <div className="auth__field">
            {/* The help text sits OUTSIDE the label on purpose. Wrapped inside
                it, the input's accessible name became the label AND the whole
                paragraph — so a screen reader announced "Date of birth We use
                this to time messages you schedule by age, like one that
                arrives on a grandchild's eighteenth birthday, it is never
                shared" as the field's name. On a product built for people in
                their seventies that is the wrong end of the trade. It is
                referenced by aria-describedby instead, which is what
                describedby is for: read after the name, not as it. */}
            <label className="auth__label" htmlFor="signup-dob">Date of birth</label>
            <input
              id="signup-dob"
              className="auth__input"
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              aria-describedby="dob-help"
              required
            />
            {/* Asking for a birthday without saying why reads as data-harvesting
                on a product whose whole promise is restraint. It is also load
                bearing: it times anything scheduled by age. */}
            <span className="auth__help" id="dob-help">
              We use this to time messages you schedule by age — like one that
              arrives on a grandchild&apos;s eighteenth birthday. It is never shared.
            </span>
          </div>
          <label className="auth__field">
            <span className="auth__label">Mobile (for security codes)</span>
            <input className="auth__input" type="tel" inputMode="tel" placeholder="+15551234567"
                   value={phone} onChange={(e) => setPhone(e.target.value)} required />
          </label>
          <div className="la-cta-wrap">
            <button className="la-btn-primary" type="submit" disabled={busy || invite === null}>
              {busy ? 'Opening…' : 'Continue'}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
